
import React from "react";
import { TableStyles } from "@/components/ui";
import { useTeams } from "../hooks/useTeams";
import { TeamStats } from "../components/TeamStats";
import { FilterSection } from "../../../components/ui/FilterSection";
import { DataTable } from "../../../components/ui/DataTable";
import { Pagination } from "../../../components/ui/Pagination";
import { TableActions } from "../../../components/ui/TableActions";
import { AddEmployeeModal } from "../components/AddEmployeeModal";
import { ViewEmployeeModal } from "../components/ViewEmployeeModal";
import { Toast } from "../../../components/ui/Toast";
import {
  formatTeamRoleLabel,
  getRoleBadgeStyle,
} from "../utils/teamMemberLabels";
import { TABLE_BACKGROUNDS } from "../../../constants/tableBackgrounds";
/*developer: Nithin
    date : 2024-06-19
    changes : Replaced Telecaller With Staff */
const ROLE_FILTER_OPTIONS = [
  { label: "All roles", value: "All Roles" },
  { label: "Owner", value: "owner" },
  { label: "Admin", value: "admin" },
  { label: "Accountant", value: "accountant" },
  { label: "Store Incharge", value: 'store_incharge' },
  { label: "Store Staff", value: "store_staff" },
];


export const TeamsScreen = () => {
  const {
    showDetails,
    setShowDetails,
    searchQuery,
    handleSearch,
    roleFilter,
    handleRoleFilter,
    installationTradeFilter,
    handleInstallationTradeFilter,
    tradeFilterOptions,
    dateRange,
    datePreset,
    handleStartDateChange,
    handleEndDateChange,
    handleDatePreset,
    teamModalOpen,
    openCreateModal,
    openEditEmployee,
    closeModal,
    editingEmployee,
    viewMode,
    setViewMode,
    isLoading,
    handleEmployeeSubmit,
    toggleMemberStatus,
    handlePageChange,
    employees,
    stats,
    page,
    totalPages,
    totalItems,
    toast,
    setToast,
    viewingEmployee,
    setViewingEmployee,
    refetchTeams,
    isExporting,
    handleExport,
  } = useTeams();

  const tableColumns = [
    {
      header: "Employee ID",
      minWidth: "80px",
      accessor: (row) => (
        <span className={TableStyles.idCell}>
          {row.employeeId || "—"}
        </span>
      ),
    },
    {
      header: "Employee Name",
      minWidth: "160px",
      accessor: (row) => (
        <div className="flex items-center gap-2.5 min-w-0">
          {/* <div
            className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px] shrink-0 shadow-sm border ${
              row.status === "Active"
                ? "bg-slate-900 text-white border-slate-700/60"
                : "bg-slate-500 text-white border-slate-600/60"
            }`}
          >
            {row.firstName?.substring(0, 1)?.toUpperCase() || "U"}
          </div> */}
          <div className="flex flex-col min-w-0">
            <span className={TableStyles.primaryText}>
              {row.firstName || "Unknown"} {row.lastName || ""}
            </span>
            <span 
              className={`${TableStyles.secondaryText} truncate max-w-[150px] block`}
              title={row.email || ""}
            >
              {(row.email?.includes('@spacedesigns.internal')) ? "—" : (row.email || "—")}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Role",
      minWidth: "120px",
      accessor: (row) => (
        <div className="flex flex-col gap-0.5">
          {/* <span
            className={`inline-flex w-fit items-center px-2.5 py-0.5 rounded-full border text-[11px] font-medium ${getRoleBadgeStyle(row.role)}`}
          >
            {formatTeamRoleLabel(row.role)}
          </span>
          {row.role === 'installation_team' && (
            <span className="text-[9px] font-bold text-slate-400 pl-0.5">
              {formatInstallationTrade(row.installationTrade)}
            </span>
          )} */}
          <span className={`inline-flex w-fit items-center px-2.5 py-0.5 rounded-full border text-[11px] font-medium ${getRoleBadgeStyle(row.role)}`}>
            {formatTeamRoleLabel(row.role)}
          </span>
        </div>
      ),
    },
    {
      header: "Phone",
      minWidth: "90px",
      accessor: (row) => (
        <span className={TableStyles.regularText}>
          {row.phone}
        </span>
      ),
    },
    {
      header: "Store Location",
      minWidth: "90px",
      headerClassName: "w-[110px]",
      className: "w-[110px]",
      accessor: (row) => (
        <span 
          className={`${TableStyles.regularText} max-w-[100px] block truncate`}
          title={row.workLocation || ""}
        >
          {row.workLocation}
        </span>
      ),
    },
    {
      header: "Created Date",
      minWidth: "85px",
      headerClassName: "w-[100px]",
      className: "w-[100px]",
      accessor: (row) => (
        <span className={TableStyles.regularText}>
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
        </span>
      ),
    },
    {
      header: "Updated Date",
      minWidth: "85px",
      headerClassName: "w-[100px]",
      className: "w-[100px]",
      accessor: (row) => (
        <span className={TableStyles.regularText}>
          {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
        </span>
      ),
    },
    {
      header: "Status",
      minWidth: "80px",
      accessor: (row) => {
        const isActive = row.status === "Active";
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${isActive
                ? "bg-emerald-50 text-emerald-700 border-emerald-200/30"
                : "bg-slate-50 text-slate-500 border-slate-200/30"
              }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? "bg-emerald-500" : "bg-slate-400"}`}
            />
            {row.status}
          </span>
        );
      },
    },
  ];

  const ActionCell = ({ row }) => {
    // Admin rows should be treated like regular users; status determines inactivity.
    const isInactiveStatus = row.status === "Inactive";
    return (
      <TableActions
        onView={!isInactiveStatus ? () => setViewingEmployee(row) : null}
        onEdit={!isInactiveStatus ? () => openEditEmployee(row) : null}
        onToggle={() => toggleMemberStatus(row)}
        onWhatsapp={() => window.open(`https://wa.me/${row.phone?.replace(/[^0-9]/g, '')}`, '_blank')}
        isActive={row.status === "Active"}
      />
    );
  };

  const columnsWithActions = [
    ...tableColumns,
    {
      header: "Actions",
      minWidth: "160px",
      accessor: (row) => <ActionCell row={row} />,
    },
  ];

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Brand Hero Banner */}
      {/* <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
            Space Designs Team
          </h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">
            Space Designs Team Management
          </p>
        </div>

        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src={require('../../../assets/Images/8.jpg')}
            alt="Team Management"
            className="absolute top-0 right-0 w-full h-full object-cover object-center"
          />
        </div>
      </div> */}

      {/* <TeamStats stats={stats} /> */}

      <div className="flex flex-col gap-2">
        <div className="flex flex-col min-w-0 bg-transparent">
          <FilterSection
            search={searchQuery}
            setSearch={handleSearch}
            addLabel="Add Team"
            onAddClick={openCreateModal}
            viewStyle={viewMode}
            setViewStyle={setViewMode}
            filters={{
              exactDate: dateRange.from === dateRange.to ? dateRange.from : "",
              date: datePreset,
            }}
            setFilters={(key, val) => {
              if (key === "exactDate") {
                handleStartDateChange(val);
                handleEndDateChange(val);
              }
              if (key === "date") handleDatePreset(val);
            }}
            onExport={handleExport}
            isExporting={isExporting}
            customActions={null}
          >
            {/* Custom Dropdown Filters */}
            <div className="flex items-center gap-2 shrink-0">

              <select
                className="text-[11px] font-bold uppercase tracking-wider bg-white border border-slate-200 hover:border-slate-800 rounded-md px-2.5 py-1.5 focus:outline-none focus:border-slate-800 text-slate-700 min-w-[130px] transition-all cursor-pointer shadow-sm"
                value={roleFilter}
                onChange={(e) => handleRoleFilter(e.target.value)}
              >
                {ROLE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label === "All roles"
                      ? "ROLE: ALL"
                      : opt.label.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </FilterSection>
        </div>
        {viewMode === "list" || viewMode === "table" ? (
          <div className="min-w-0 overflow-x-auto">
            <DataTable
              columns={columnsWithActions}
              data={employees}
              backgroundImage={TABLE_BACKGROUNDS.teams}
              isLoading={isLoading}
              isRowInactive={(row) => row.status === "Inactive"}
              getRowClassName={(row) =>
                row.status === "Inactive"
                  ? "bg-slate-100/90 border-y border-slate-200/80"
                  : "hover:bg-slate-50/50"
              }
            />
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-[400px] text-slate-500">
            Grid view is under construction.
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 bg-white rounded-2xl shadow-xs py-2 px-4 mt-2">
        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          onPageChange={handlePageChange}
        />
      </div>

      {teamModalOpen && (
        <AddEmployeeModal
          key={editingEmployee?._id || "create"}
          isOpen
          onClose={closeModal}
          onSubmit={handleEmployeeSubmit}
          isLoading={isLoading}
          initialData={editingEmployee}
        />
      )}

      <ViewEmployeeModal
        employee={viewingEmployee}
        onClose={() => setViewingEmployee(null)}
        onEmployeeUpdated={(updated) => {
          setViewingEmployee((prev) =>
            prev && updated ? { ...prev, ...updated } : prev,
          );
          refetchTeams();
        }}
      />
    </div>
  );
};




